/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { confirmDialog } from "primereact/confirmdialog";
import {
  accessAPI,
  downloadFile,
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  TablaFiltrable
} from "../../../../shared";

export default function CardEquivalencias({
  normaId,
  equivalencias,
  onChange,
  toastRef
}) {
  const [sidebarNueva, setSidebarNueva] = useState(false);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [loaderNueva, setLoaderNueva] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [equivalenciaSeleccionada, setEquivalenciaSeleccionada] = useState(null);
  const [formKeyNueva, setFormKeyNueva] = useState(0);
  const [formKeyEditar, setFormKeyEditar] = useState(0);
  const menuRef = useRef(null);

  function agregarEquivalencia(datos) {
    setLoaderNueva(true);
    accessAPI(
      "POST",
      `admin/norma/${normaId}/equivalencias`,
      {
        normaEquivalente: datos.normaEquivalente,
        documentoId: datos.documentoId || null,
        notas: datos.notas || null
      },
      (respuesta) => {
        setLoaderNueva(false);
        setSidebarNueva(false);
        onChange([...(equivalencias || []), respuesta]);
        toastRef?.current?.show({
          severity: "success",
          summary: "Agregado",
          detail: "Equivalencia agregada"
        });
      },
      (respuesta) => {
        setLoaderNueva(false);
        toastRef?.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar la equivalencia",
          sticky: true
        });
      }
    );
  }

  function editarEquivalencia(datos) {
    if (!equivalenciaSeleccionada) return;
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/norma/${normaId}/equivalencias/${equivalenciaSeleccionada.id}`,
      {
        normaEquivalente: datos.normaEquivalente,
        documentoId: datos.documentoId || null,
        notas: datos.notas || null
      },
      (respuesta) => {
        setLoaderEditar(false);
        setSidebarEditar(false);
        onChange(
          (equivalencias || []).map((eq) =>
            eq.id === respuesta.id ? respuesta : eq
          )
        );
        toastRef?.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Equivalencia actualizada"
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        toastRef?.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar la equivalencia",
          sticky: true
        });
      }
    );
  }

  function eliminarEquivalencia(equivalencia) {
    confirmDialog({
      message: `¿Eliminar la equivalencia "${equivalencia.normaEquivalente}"?`,
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/norma/${normaId}/equivalencias/${equivalencia.id}`,
          null,
          () => {
            onChange(
              (equivalencias || []).filter((eq) => eq.id !== equivalencia.id)
            );
            toastRef?.current?.show({
              severity: "success",
              summary: "Eliminado",
              detail: "Equivalencia eliminada"
            });
          },
          (respuesta) => {
            toastRef?.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al eliminar la equivalencia",
              sticky: true
            });
          }
        );
      }
    });
  }

  function descargarArchivo(equivalencia) {
    const version = equivalencia.documentoEquivalencia?.documentoVersions?.[0];
    if (!version) return;
    const archivoId = version.archivoPDFId || version.archivoId;
    const nombre =
      version.archivoPDF?.nombredescarga || version.archivo?.nombredescarga;
    if (!archivoId) return;
    downloadFile(
      `admin/archivo/${archivoId}`,
      nombre,
      () => {},
      (error) => {
        toastRef?.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al descargar archivo",
          sticky: true
        });
      }
    );
  }

  const nuevaFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "normaEquivalente",
            tipoitemformularioid: 1,
            etiqueta: "Norma equivalente",
            requerido: true
          },
          {
            nombre: "documentoId",
            tipoitemformularioid: 5,
            etiqueta: "Archivo",
            opciones: "admin/norma/equivalencias/documentos",
            opcionVacia: true
          },
          {
            nombre: "notas",
            tipoitemformularioid: 11,
            etiqueta: "Notas"
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar equivalencia",
        callback: agregarEquivalencia,
        validar: true
      }
    ]
  };

  const editarFormConfig = equivalenciaSeleccionada
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "normaEquivalente",
                tipoitemformularioid: 1,
                etiqueta: "Norma equivalente",
                requerido: true,
                respuesta: equivalenciaSeleccionada.normaEquivalente
              },
              {
                nombre: "documentoId",
                tipoitemformularioid: 5,
                etiqueta: "Archivo",
                opciones: "admin/norma/equivalencias/documentos",
                opcionVacia: true,
                respuesta: equivalenciaSeleccionada.documentoId
              },
              {
                nombre: "notas",
                tipoitemformularioid: 11,
                etiqueta: "Notas",
                respuesta: equivalenciaSeleccionada.notas
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarEquivalencia,
            validar: true
          }
        ]
      }
    : null;

  const tieneArchivo = !!equivalenciaSeleccionada?.documentoEquivalencia
    ?.documentoVersions?.[0];

  const menuItems = [
    {
      label: "Editar",
      icon: "pi pi-pencil",
      command: () => {
        setFormKeyEditar((p) => p + 1);
        setSidebarEditar(true);
      }
    },
    ...(tieneArchivo
      ? [
          {
            label: "Descargar archivo",
            icon: "pi pi-download",
            command: () => descargarArchivo(equivalenciaSeleccionada)
          }
        ]
      : []),
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "text-red-500",
      command: () => {
        if (equivalenciaSeleccionada)
          eliminarEquivalencia(equivalenciaSeleccionada);
      }
    }
  ];

  return (
    <>
      <Menu model={menuItems} popup ref={menuRef} />

      <Card css={{ marginTop: "1em" }}>
        <Titulo
          botones={
            <Boton
              icono
              negro
              callback={() => {
                setFormKeyNueva((p) => p + 1);
                setSidebarNueva(true);
              }}
            >
              <i className="pi pi-plus" />
            </Boton>
          }
        >
          Equivalencias
        </Titulo>
        <TablaFiltrable conFiltros={false}
          value={equivalencias || []}
          nombreArchivo="equivalencias_norma"
          size="small"
          emptyMessage="No hay equivalencias cargadas"
        >
          <Column field="normaEquivalente" header="Norma equivalente" />
          <Column
            header="Archivo"
            body={(rowData) => {
              const doc = rowData.documentoEquivalencia;
              if (!doc)
                return <span css={{ color: "var(--grisTextos)" }}>—</span>;
              return `${doc.codigo} - ${doc.nombre}`;
            }}
          />
          <Column
            body={(rowData) => {
              if (!rowData.notas) return null;
              const tooltipId = `equivalencia-notas-${rowData.id}`;
              return (
                <>
                  <Tooltip
                    target={`#${tooltipId}`}
                    content={rowData.notas}
                    position="top"
                  />
                  <i
                    id={tooltipId}
                    className="pi pi-info-circle"
                    style={{
                      color: "var(--amarillo)",
                      fontSize: "1.1rem",
                      cursor: "pointer"
                    }}
                  />
                </>
              );
            }}
            style={{ width: "3rem", textAlign: "center" }}
          />
          <Column
            body={(rowData) => (
              <Boton
                icono
                negro
                chico
                callback={(e) => {
                  setEquivalenciaSeleccionada(rowData);
                  menuRef.current.toggle(e);
                }}
              >
                <i className="pi pi-ellipsis-v" />
              </Boton>
            )}
            style={{ width: "3rem", textAlign: "center" }}
          />
        </TablaFiltrable>
      </Card>

      <Sidebar
        visible={sidebarNueva}
        onHide={() => setSidebarNueva(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nueva equivalencia</Titulo>
        <Formulario
          config={nuevaFormConfig}
          loader={loaderNueva}
          key={`nueva-${formKeyNueva}`}
        />
      </Sidebar>

      <Sidebar
        visible={sidebarEditar}
        onHide={() => setSidebarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar equivalencia</Titulo>
        {editarFormConfig && (
          <Formulario
            config={editarFormConfig}
            loader={loaderEditar}
            key={`editar-${formKeyEditar}`}
          />
        )}
      </Sidebar>
    </>
  );
}
